import { of, concat, from } from "rxjs";
import { getPhotoForUserId, validateValue } from "../../utils/utils";
import { api, apiConfig } from "../api/api";
import { mainRouter } from "../../routers/AppRouter";
import * as moment from "moment";
import {
  mergeMap,
  map,
  catchError,
  filter,
  switchMap,
  debounceTime,
  withLatestFrom,
} from "rxjs/operators";
import { combineEpics, ofType } from "redux-observable";
import {
  ballSet,
  ballError,
  ballHandleForm,
  ballsListLoad,
  ballSavePhoto,
  ballPhotoSaved,
  types,
  ballSelect,
  ballSelected,
  setCurrentJobs,
  setJob,
  jobSelected,
  jobDetailCreated,
  jobDetailDeleted,
  jobDeleted,
  jobDelete,
  getBallDatabaseSearchSuccess,
  getBallDatabaseFiltersSuccess,
  setBallDatabaseInfo,
  getBallDatabaseInfoSuccess,
  setOpenBallDatabaseSearch,
} from "./arsenal.redux";
import { getJobBoardDashboard } from "../dashboard/dashboard.redux";
import {
  types as bowlersTypes,
  updateBall,
  getCustomer,
} from "../bowlers/bowlers.redux";
import { datasources } from "../db/datasources";
import Notify from "../ui/Notify";
import i18n from "../i18n";
import uuid from "uuid/v1";
import * as R from "ramda";
import * as Promise from "bluebird";

Promise.config({ warnings: false, longStackTraces: false, asyncHooks: false });

const parseExtraValuesToObject = (v) => {
  if (!v) return {};
  if (typeof v === "object") return v;
  if (typeof v !== "string") return {};
  let curr = v;
  for (let i = 0; i < 20; i++) {
    try {
      const next = JSON.parse(curr);
      if (next && typeof next === "object") return next;
      if (typeof next === "string") {
        curr = next;
        continue;
      }
      return {};
    } catch (_) {
      return {};
    }
  }
  return {};
};

const mergeExtraValues = (existing, patch) => ({
  ...parseExtraValuesToObject(existing),
  ...patch,
});

const safeBallUpdate = (ds, ball) =>
  ds.update({
    ...ball,
    extra_values: parseExtraValuesToObject(ball.extra_values),
  });

const getLayout = (currentLayout) => (ball) => {
  switch (currentLayout) {
    case "dual_angle":
      return [
        ball.drilling_angle || "",
        ball.pin_to_pap || "",
        ball.val_angle || "",
      ].join(" X ");
    case "vls":
      return [
        ball.pin_to_pap || "",
        ball.psa_to_pap || "",
        ball.pin_buffer || "",
      ].join(" X ");
    case "none":
    default:
      return ball.layout;
  }
};

const ballsHandleFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballHandleForm),
    debounceTime(500),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { ball },
        },
        state,
      ]) =>
        from(
          new Promise((success, reject) => {
            // ball.layout = getLayout(
            //   state$.value.settings.appSettings.layoutPreferences
            //     .arsenal_layout_style
            // )(ball);
            const { ballDrilledDS } = datasources;
            if (ball.id) {
              safeBallUpdate(ballDrilledDS, {
                ...ball,
                customer_id: state.bowlers.selectedCustomer.id,
                customer: undefined,
                jobs: undefined,
                spec_sheet: undefined,
                localId: ball.id,
              })
                .then((ball) => {
                  success(ball);
                })
                .catch((error) => {
                  console.warn(error);
                  reject(error);
                });
            } else {
              const id = uuid();
              ballDrilledDS
                .create({
                  ...ball,
                  customer_id: state.bowlers.selectedCustomer.id,
                  date_drilled: moment(),
                  id,
                })
                .then((ball) => {
                  mainRouter.navigate(
                    "/arsenal/" +
                      state.bowlers.selectedCustomer.id.toString() +
                      "/ball/" +
                      id.toString()
                  );
                  success(ball);
                })
                .catch((error) => {
                  console.warn(error);
                  reject(error);
                });
            }
          })
        ).pipe(
          switchMap((ball) => {
            return concat(
              of(
                updateBall({
                  ball: {
                    ...ball,
                    customer_id: state.bowlers.selectedCustomer.id,
                  },
                })
              ),
              of(
                ballSet({
                  ball: {
                    ...ball,
                    customer_id: state.bowlers.selectedCustomer.id,
                  },
                })
              )
            );
          })
        )
    )
  );

const ballsStateChangeEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballChangeState),
    mergeMap(({ payload: { ball, state } }) => {
      const { ballDrilledDS } = datasources;
      return safeBallUpdate(ballDrilledDS, {
        ...ball,
        localId: ball.id,
        enable: state,
      }).then(() => ballsListLoad());
    })
  );

const ballsRemoveEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballRemove),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { id },
        },
        state,
      ]) => {
        const { ballDrilledDS } = datasources;
        return ballDrilledDS.delete(id).then(() => {
          return getCustomer({
            customer_id: state.bowlers.selectedCustomer.id,
          });
        });
      }
    )
  );

const ballsPhotoSavedEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballPhotoSaved),
    mergeMap(() => {
      return of(ballHandleForm({ ball: state$.value.balls.ball }));
    })
  );

const comparatorCaseInsensitive = (property) => R.compose(R.prop(property));

const sortByOrderCaseInsensitive = R.sortWith([
  R.ascend(comparatorCaseInsensitive("position")),
]);

const ballsBowlerSelectEpic = (action$, state$) =>
  action$.pipe(
    ofType(bowlersTypes.bowlerSelect),
    mergeMap(() => of(ballsListLoad({ selectFirst: true })))
  );

const ballsAddNewEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballAddNew),
    withLatestFrom(state$),
    mergeMap(([_, state]) =>
      from(
        new Promise((success, reject) => {
          const { ballDrilledDS } = datasources;

          const id = uuid();

          ballDrilledDS
            .create({
              localId: id,
              customer_id: state.bowlers.selectedCustomer.id,
              ball_name: "",
              serial_number: "",
              layout: "",
              photo: "",
              enable: true,
              date_drilled: moment(),
            })
            .then((ball) => {
              return success(ball);
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map((ball) => {
          return ballSelect({ ball: ball });
        }),
        catchError((error) => {
          console.warn(error);
          return ballError({ error: {} });
        })
      )
    )
  );

const ballsRemovePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballRemovePhoto),
    mergeMap(() =>
      of(
        ballHandleForm({
          ball: { ...state$.value.balls.ball, photo: undefined },
        })
      )
    )
  );

const ballsSavePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballSavePhoto),
    debounceTime(500),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { imageBase64 },
        },
        state,
      ]) => {
        const ball = state.balls.currentBall;
        const userId = state.userSession.userData.id;
        let fileName = "arsenal_" + ball.id + "_" + uuid() + ".png";

        const getPhoto = getPhotoForUserId(userId);
        const photo = getPhoto(fileName);

        const savebase64AsImageFile = (fileName, base64) => {
          return new Promise((success, fail) => {
            // console.log("Image saved!!");
            const { ballDrilledDS } = datasources;
            const { getSessionApiUrl, checkOffline, ajaxSession } = api;

            const sendImageToServer = ({ image, imageFileName }) =>
              R.pipeP(
                () => Promise.resolve("images/uploadImageBase64"),
                (url) => {
                  // console.log("url", url);
                  return ajaxSession(url, {
                    type: "POST",
                    noWorker: true,
                    data: {
                      image,
                      imageFileName,
                    },
                  });
                }
              )();

            const saveFileOnServer = (fileProps) =>
              R.pipeP(checkOffline, (offline) =>
                offline ? Promise.resolve() : sendImageToServer(fileProps)
              )();

            saveFileOnServer({ image: base64, imageFileName: fileName })
              .then(() => {
                return safeBallUpdate(ballDrilledDS, {
                  ...ball,
                  photo,
                  customer: undefined,
                  jobs: undefined,
                  spec_sheet: undefined,
                });
              })
              .then(() => {
                success(getPhoto(fileName));
                // console.log("Image saved success!!");
              })
              .catch(fail);
          });
        };

        return from(savebase64AsImageFile(fileName, imageBase64)).pipe(
          switchMap((photo) => {
            return concat(
              of(updateBall({ ball: { ...ball, photo } })),
              of(ballSet({ ball: { ...ball, photo } }))
            );
          }),
          catchError(() => {
            return concat(of(updateBall({ ball })), of(ballSet({ ball })));
          })
        );
      }
    )
  );

const ballsChangeOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballsChangeOrder),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const { ballDrilledDS } = datasources;
      const pUpdate = state.balls.balls.map((ball, index) =>
        safeBallUpdate(ballDrilledDS, {
          ...ball,
          localId: ball.id,
          position: index,
        })
      );
      return Promise.all(pUpdate).then(() => ballsListLoad());
    })
  );

const ballSelectEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.ballSelect),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success) => {
          if (payload.ball) {
            const bowlerID = state.bowlers.selectedCustomer.id;
            const id = payload.ball.id;
            //absolute route
            if (validateValue(id) !== "") {
              mainRouter.navigate(
                "/arsenal/" + bowlerID.toString() + "/ball/" + id.toString()
              );
            } else {
              mainRouter.navigate("/arsenal/" + bowlerID.toString());
            }
          }
          success();
        })
      ).pipe(
        map(() => {
          return ballSelected();
        }),
        catchError((error) => {
          console.warn(error);
          return of(ballSelected());
        })
      )
    )
  );

// const jobSelectEpic = (action$, state$) =>
//   action$.pipe(
//     ofType(types.jobSelect),
//     withLatestFrom(state$),
//     mergeMap(([{ payload }, state]) =>
//       from(
//         new Promise((success) => {
//           if (payload.job) {
//             const bowlerID = state.bowlers.selectedCustomer.id;
//             const ballId = state.balls.currentBall.id;
//             const id = payload.job.id;

//             //absolute route
//             if (validateValue(id) !== "") {
//               mainRouter.navigate(
//                 "/arsenal/" +
//                   bowlerID.toString() +
//                   "/ball/" +
//                   ballId.toString() +
//                   "/job/" +
//                   id.toString()
//               );
//             } else {
//               mainRouter.navigate("/arsenal/" + bowlerID.toString());
//             }
//           }
//           success();
//         })
//       ).pipe(
//         map(() => {
//           return jobSelected();
//         }),
//         catchError((error) => {
//           console.warn(error);
//           return of(jobSelected());
//         })
//       )
//     )
//   );

const createJobEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.createJob),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const { jobDS } = datasources;
          const id = uuid();
          jobDS
            .create({
              localId: id,
              ball_id: state.balls.currentBall.id,
              date_created: moment(),
              date_due: moment(),
              status:
                state.plugin.plugin_settings.job_board_default_state ||
                "pending",
            })
            .then((job) => {
              mainRouter.navigate(
                "/arsenal/" +
                  state.bowlers.selectedCustomer.id.toString() +
                  "/ball/" +
                  state.balls.currentBall.id.toString() +
                  "/job/" +
                  job.id.toString()
              );
              return success(job);
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map((job) => {
          return setJob({ job });
        }),
        catchError((error) => {
          console.warn(error);
          return of(setJob({}));
        })
      )
    )
  );

const jobHandleFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.jobHandleForm),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const { jobDS } = datasources;
          const id = uuid();
          jobDS
            .update({
              ...R.omit(
                ["updated_at", "created_at", "created_by", "updated_by"],
                payload.job
              ),
              date_due: moment(payload.job.date_due),
              date_created: moment(payload.job.date_created),
              discount: payload.job.discount ? payload.job.discount * 1 : 0,
            })
            .then((job) => {
              return success(job);
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        switchMap((job) => {
          return concat(
            of(
              setJob({
                job: {
                  ...job,
                  sales_person: job?.sales_person,
                  job_location: job?.job_location,
                },
              })
            ),
            of(getJobBoardDashboard({}))
          );
        }),
        catchError((error) => {
          console.warn(error);
          return of(setJob({}));
        })
      )
    )
  );

/**
 * "Your ball is ready" -- sent only when the driller says so.
 *
 * The recipient is not in the payload: the server resolves it from the job,
 * so a stale dashboard row cannot mail the wrong bowler. Failure is a toast,
 * never a rollback -- the status change already happened and is correct.
 */
const jobNotifyCustomerEpic = (action$) =>
  action$.pipe(
    ofType(types.jobNotifyCustomer),
    mergeMap(({ payload: { jobId, extraNote } }) => {
      const { ajaxSession } = api;

      return from(
        ajaxSession("job/notifyStatus", {
          type: "POST",
          noWorker: true,
          data: { job_id: jobId, extraNote: extraNote || "" },
        })
      ).pipe(
        mergeMap(() => {
          Notify.success(i18n.t("jobBoard.notifyCustomerSent"));
          return of({ type: "NO_OP" });
        }),
        catchError((error) => {
          console.warn(error);
          Notify.error(i18n.t("jobBoard.notifyCustomerFailed"));
          return of({ type: "NO_OP" });
        })
      );
    })
  );

const createJobDetailEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.jobDetailCreate),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const { jobDetailDS } = datasources;
          const id = uuid();
          jobDetailDS
            .create({
              localId: id,
              job_id: state.balls.currentJob.id,
              job_service_id: payload.service_id,
              price: payload.price,
            })
            .then((jobDetail) => {
              return success(jobDetail);
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map((jobDetail) => {
          return jobDetailCreated({
            jobDetail: {
              ...jobDetail,
              job_service: { id: payload.service_id },
            },
          });
        }),
        catchError((error) => {
          console.warn(error);
          return of(jobDetailCreated({ jobDetail: {} }));
        })
      )
    )
  );

const deleteJobDetailEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.jobDetailDelete),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const { jobDetailDS } = datasources;
          jobDetailDS
            .delete(payload.id)
            .then((jobDetail) => {
              return success(jobDetail);
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map(() => {
          return jobDetailDeleted({
            id: payload.id,
            job_service_id: payload.job_service_id,
          });
        }),
        catchError((error) => {
          console.warn(error);
          return of(jobDetailDeleted({ id: "" }));
        })
      )
    )
  );

const deleteJobEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.jobDelete),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const { jobDS } = datasources;
          jobDS
            .delete(payload.id)
            .then(() => {
              const bowlerID = state.bowlers.selectedCustomer.id;
              const ballId = state.balls.currentBall.id;

              mainRouter.navigate(
                "/arsenal/" + bowlerID.toString() + "/ball/" + ballId.toString()
              );
              return success();
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map(() => {
          return jobDeleted({ id: payload.id });
        }),
        catchError((error) => {
          console.warn(error);
          return of(jobDeleted({ id: "" }));
        })
      )
    )
  );

const getBallDatabaseInfoScanEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getBallDatabaseInfoScan),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const customer_id = state.bowlers.selectedCustomer.id;
          // console.log("apiConfig", apiConfig);
          const apiBallDatabase = apiConfig.bowlingDatabase;
          const { ajax } = api;

          ajax("/balls/", {
            url: apiBallDatabase,
            params: {
              filter: JSON.stringify({
                // q: payload.q,
                manufacturer_item_number: payload.q,
              }),
              range: `[0, 2]`,
            },
            type: "GET",
            headers: {
              "x-access-token": apiConfig.bowlingDatabaseKey,
            },
            data: {},
          })
            .then((balls) => {
              // console.log("balls found:", balls);
              if (balls.length > 0) {
                const ballInfo = balls[0];
                const { ballDrilledDS } = datasources;
                const ball = state.balls.currentBall;
                const scannedBarcode =
                  ballInfo.manufacturer_item_number ||
                  ballInfo.barcode ||
                  ballInfo.upc ||
                  payload.q;

                safeBallUpdate(ballDrilledDS, {
                  ...ball,
                  ball_name: ballInfo.name,
                  manufacturer: ballInfo.manufacturer,
                  diff: ballInfo.diff,
                  int_diff: ballInfo.int,
                    rg: ballInfo.rg,
                    core: ballInfo.core_name,
                    cover: ballInfo.coverstock,
                    weight: ballInfo.size,
                    customer_id,
                    customer: undefined,
                    jobs: undefined,
                    spec_sheet: undefined,
                    extra_values: scannedBarcode
                      ? mergeExtraValues(ball.extra_values, {
                          barcode: scannedBarcode,
                        })
                      : ball.extra_values,
                  })
                  .then((ball) => {
                    const userId = state.userSession.userData.id;
                    let fileName = "arsenal_" + ball.id + "_" + uuid() + ".png";

                    const getPhoto = getPhotoForUserId(userId);
                    const photo = getPhoto(fileName);

                    const downloadImage = async (imageSrc) => {
                      if (!imageSrc || imageSrc === "") {
                        return "";
                      }

                      try {
                        const image = await fetch(imageSrc);
                        const imageBlob = await image.blob();

                        const blobToBase64 = (blob) => {
                          return new Promise((resolve, _) => {
                            const reader = new FileReader();
                            reader.onloadend = () =>
                              resolve(reader.result.split(";base64,")[1]);
                            reader.readAsDataURL(blob);
                          });
                        };

                        return await blobToBase64(imageBlob);
                      } catch (error) {
                        console.warn(error);
                        return "";
                      }
                    };

                    const savebase64AsImageFile = (fileName, base64) => {
                      return new Promise((success, fail) => {
                        // console.log("Image saved!!");
                        const { getSessionApiUrl, checkOffline, ajaxSession } =
                          api;

                        const sendImageToServer = ({ image, imageFileName }) =>
                          R.pipeP(
                            () => Promise.resolve("images/uploadImageBase64"),
                            (url) => {
                              // console.log("url", url);
                              return ajaxSession(url, {
                                type: "POST",
                                noWorker: true,
                                data: {
                                  image,
                                  imageFileName,
                                },
                              });
                            }
                          )();

                        const saveFileOnServer = (fileProps) =>
                          R.pipeP(checkOffline, (offline) =>
                            offline
                              ? Promise.resolve()
                              : sendImageToServer(fileProps)
                          )();

                        saveFileOnServer({
                          image: base64,
                          imageFileName: fileName,
                        })
                          .then(() => {
                            // console.log(photo);
                            ballDrilledDS
                              .update({
                                ...ball,
                                photo,
                                customer_id,
                                customer: undefined,
                                jobs: undefined,
                                spec_sheet: undefined,
                              })
                              .then(success);
                          })
                          .catch(fail);
                      });
                    };

                    return downloadImage(ballInfo.image_url)
                      .then((base64) => {
                        return savebase64AsImageFile(fileName, base64)
                          .then((ball) => {
                            // console.log("Image saved success!!");
                            return success({ ...ball, customer_id });
                          })
                          .catch((error) => {
                            console.warn(error);
                            reject(error);
                          });
                      })
                      .catch((error) => {
                        console.warn(error);
                        reject(error);
                      });
                  })
                  .catch((error) => {
                    console.warn(error);
                    reject(error);
                  });
              } else {
                reject("not found");
              }
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        switchMap((ball) => {
          return concat(
            of(
              setOpenBallDatabaseSearch({
                open: false,
                settingBallDatabaseInfo: true,
              })
            ),
            of(
              updateBall({
                ball: {
                  ...ball,
                  customer_id: state.bowlers.selectedCustomer.id,
                },
              })
            ),
            of(
              ballSet({
                ball: {
                  ...ball,
                  customer_id: state.bowlers.selectedCustomer.id,
                },
              })
            )
          );
        }),
        catchError((error) => {
          console.warn(error);
          return of(
            setOpenBallDatabaseSearch({
              open: true,
              settingBallDatabaseInfo: false,
            })
          );
        })
      )
    )
  );

const getBallDatabaseInfoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getBallDatabaseInfo),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          // console.log("apiConfig", apiConfig);
          const apiBallDatabase = apiConfig.bowlingDatabase;
          const { ajax } = api;

          Promise.all(
            payload.ids.split("|").map((id) =>
              ajax(`/balls/${id}`, {
                url: apiBallDatabase,
                params: {},
                type: "GET",
                headers: {
                  "x-access-token": apiConfig.bowlingDatabaseKey,
                },
                data: {},
              })
            )
          )
            .then((balls) => {
              success(balls);
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map((balls) => {
          return getBallDatabaseInfoSuccess({ balls });
        }),
        catchError((error) => {
          console.warn(error);
          return of(getBallDatabaseInfoSuccess({ balls: [] }));
        })
      )
    )
  );

const setBallDatabaseInfoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.setBallDatabaseInfo),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const customer_id = state.bowlers.selectedCustomer.id;
          const ballInfo = payload.ball;
          const { ballDrilledDS } = datasources;
          const ball = state.balls.currentBall;

          const pickedBarcode =
            ballInfo.manufacturer_item_number ||
            ballInfo.barcode ||
            ballInfo.upc;

          safeBallUpdate(ballDrilledDS, {
            ...ball,
            ball_name: ballInfo.name,
            manufacturer: ballInfo.manufacturer,
            diff: ballInfo.diff,
            int_diff: ballInfo.int,
            rg: ballInfo.rg,
            core: ballInfo.core_name,
            cover: ballInfo.coverstock,
            weight: ballInfo.size,
            customer_id,
            customer: undefined,
            jobs: undefined,
            spec_sheet: undefined,
            extra_values: pickedBarcode
              ? mergeExtraValues(ball.extra_values, {
                  barcode: pickedBarcode,
                })
              : parseExtraValuesToObject(ball.extra_values),
          })
            .then((ball) => {
              const userId = state.userSession.userData.id;
              let fileName = "arsenal_" + ball.id + "_" + uuid() + ".png";

              const getPhoto = getPhotoForUserId(userId);
              const photo = getPhoto(fileName);

              const downloadImage = async (imageSrc) => {
                try {
                  const image = await fetch(imageSrc);
                  const imageBlob = await image.blob();

                  const blobToBase64 = (blob) => {
                    return new Promise((resolve, _) => {
                      const reader = new FileReader();
                      reader.onloadend = () =>
                        resolve(reader.result.split(";base64,")[1]);
                      reader.readAsDataURL(blob);
                    });
                  };

                  return await blobToBase64(imageBlob);
                } catch (error) {
                  console.warn(error);
                  return "";
                }
              };

              const savebase64AsImageFile = (fileName, base64) => {
                return new Promise((success, fail) => {
                  // console.log("Image saved!!");
                  const { getSessionApiUrl, checkOffline, ajaxSession } = api;

                  const sendImageToServer = ({ image, imageFileName }) =>
                    R.pipeP(
                      () => Promise.resolve("images/uploadImageBase64"),
                      (url) => {
                        // console.log("url", url);
                        return ajaxSession(url, {
                          type: "POST",
                          noWorker: true,
                          data: {
                            image,
                            imageFileName,
                          },
                        });
                      }
                    )();

                  const saveFileOnServer = (fileProps) =>
                    R.pipeP(checkOffline, (offline) =>
                      offline ? Promise.resolve() : sendImageToServer(fileProps)
                    )();

                  saveFileOnServer({
                    image: base64,
                    imageFileName: fileName,
                  })
                    .then(() => {
                      // console.log(photo);
                      safeBallUpdate(ballDrilledDS, {
                        ...ball,
                        photo,
                        customer_id,
                        customer: undefined,
                        jobs: undefined,
                        spec_sheet: undefined,
                      }).then(success);
                    })
                    .catch(fail);
                });
              };

              return downloadImage(ballInfo.image_url)
                .then((base64) => {
                  return savebase64AsImageFile(fileName, base64)
                    .then((ball) => {
                      // console.log("Image saved success!!");
                      return success({ ...ball, customer_id });
                    })
                    .catch((error) => {
                      console.warn(error);
                      reject(error);
                    });
                })
                .catch((error) => {
                  console.warn(error);
                  reject(error);
                });
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        switchMap((ball) => {
          return concat(
            of(
              updateBall({
                ball: {
                  ...ball,
                  customer_id: state.bowlers.selectedCustomer.id,
                },
              })
            ),
            of(
              ballSet({
                ball: {
                  ...ball,
                  customer_id: state.bowlers.selectedCustomer.id,
                },
              })
            )
          );
        }),
        catchError((error) => {
          console.warn(error);
        })
      )
    )
  );

const getBallDatabaseSearchEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getBallDatabaseSearch),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const customer_id = state.bowlers.selectedCustomer.id;
          // console.log("apiConfig", apiConfig);
          const apiBallDatabase = apiConfig.bowlingDatabase;
          const { ajax } = api;
          let ajaxClient = null;

          ajax(
            "/balls/",
            {
              url: apiBallDatabase,
              params: {
                sort: JSON.stringify(payload.order),
                range: JSON.stringify([
                  payload.rangeInitial,
                  payload.rangeFinal,
                ]),
                filter: JSON.stringify({
                  q: { val: payload.filter, filters: payload.filters },
                }),
              },
              type: "GET",
              headers: {
                "x-access-token": apiConfig.bowlingDatabaseKey,
              },
              data: {},
            },
            (aClient) => {
              ajaxClient = aClient;
            }
          )
            .then((balls) => {
              // console.log("balls found:", balls, ajaxClient);
              success({
                balls,
                totalRecords: ajaxClient.getResponseHeader("x-total-count") * 1,
              });
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map(({ balls, totalRecords }) => {
          return getBallDatabaseSearchSuccess({ balls, totalRecords });
        }),
        catchError((error) => {
          console.warn(error);
          return of(
            getBallDatabaseSearchSuccess({ balls: [], totalRecords: 0 })
          );
        })
      )
    )
  );

const getBallDatabaseFiltersEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getBallDatabaseSearch),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) =>
      from(
        new Promise((success, reject) => {
          const customer_id = state.bowlers.selectedCustomer.id;
          // console.log("apiConfig", apiConfig);
          const apiBallDatabase = apiConfig.bowlingDatabase;
          const { ajax } = api;
          let ajaxClient = null;

          ajax(
            "/filters/",
            {
              url: apiBallDatabase,
              params: {
                sort: JSON.stringify(payload.order),
                range: JSON.stringify([
                  payload.rangeInitial,
                  payload.rangeFinal,
                ]),
                filter: JSON.stringify({
                  q: { val: payload.filter, filters: payload.filters },
                }),
              },
              type: "GET",
              headers: {
                "x-access-token": apiConfig.bowlingDatabaseKey,
              },
              data: {},
            },
            (aClient) => {
              ajaxClient = aClient;
            }
          )
            .then((filters) => {
              // console.log("filters found:", filters, ajaxClient);
              success({
                filters,
              });
            })
            .catch((error) => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map(({ filters }) => {
          return getBallDatabaseFiltersSuccess({ filters });
        }),
        catchError((error) => {
          console.warn(error);
          return of(getBallDatabaseFiltersSuccess({ filters: [] }));
        })
      )
    )
  );

export default combineEpics(
  ballsHandleFormEpic,
  ballsAddNewEpic,
  ballsSavePhotoEpic,
  ballsPhotoSavedEpic,
  ballsRemovePhotoEpic,
  ballsRemoveEpic,
  ballsStateChangeEpic,
  ballsChangeOrderEpic,
  ballsBowlerSelectEpic,
  ballSelectEpic,
  createJobEpic,
  createJobDetailEpic,
  jobHandleFormEpic,
  jobNotifyCustomerEpic,
  // jobSelectEpic,
  deleteJobEpic,
  deleteJobDetailEpic,
  getBallDatabaseInfoEpic,
  getBallDatabaseInfoScanEpic,
  getBallDatabaseSearchEpic,
  setBallDatabaseInfoEpic,
  getBallDatabaseFiltersEpic
);
